import {
    useEffect,
    useState,
} from 'react';
import {I18n} from 'aws-amplify/utils';
import {NavLink} from 'react-router-dom';

import {
    Alert,
    Anchor,
    Button,
    Code,
    Group,
    Modal,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';

import {
    delegationState,
    DnsZone,
    recordCountState,
} from '../types';
import {
    zoneContentsNotice,
    zoneDeletionBlock,
} from '../hooks/useZoneDeletionGuard';
import {classifyWriteError} from '../services/writeErrors';
import {
    isWriteErrorRetryable,
    writeErrorCopy,
} from '../writeErrorMessages';
import {useGrantElevation} from '../../provider-connections/hooks/useGrantElevation';
import providerConnectionsConfig from '../../provider-connections/config';

// FNP-1191 CRUD half — THE CONFIRMATION, AND THE TWO REFUSALS.
//
// ⚠️ A CONTROLLED Mantine `Modal`, NOT `modals.openConfirmModal`. No
// `ModalsProvider` is mounted in src/App.tsx, so the latter would silently do
// nothing — the worst failure for a destructive-action guard, because the
// button would look inert rather than broken.
//
// ─── FNP-1438: WHY THIS DIALOG IS SHORT ───
//
// It reached 324 words and 1911px in an 855px viewport, confirm button below
// the fold, with a raw IAM policy document in the middle. Three tickets each
// added a defensible block (FNP-1363 policy preview, FNP-1365 elevation
// prompt, FNP-1391 record notice) and nothing measured the sum.
//
// ⚠️ THE ELEVATION PROMPT IS GONE FROM HERE, not deleted. `GrantElevationPrompt`
// still serves DeleteCertificateModal; what a delete dialog needs is one line
// naming the missing permission and a link to the connection page, which is
// where permissions are already managed. A policy document is not a decision
// anyone makes mid-delete.
//
// ⚠️ THE GUARDS SURVIVED THE TRIM. Delegation, the record notice and the
// permanence sentence each stop a different mistake; the budget was met by
// cutting explanation, not protection. deleteZoneModalVolume.test.tsx pins
// both halves so the next well-argued paragraph fails a test.

export const DeleteZoneModal = (
    {
        zone,
        opened,
        onClose,
        onConfirm,
        isDeleting = false,
        error,
    }: {
        zone?: DnsZone,
        opened: boolean,
        onClose: () => void,
        onConfirm: () => void,
        isDeleting?: boolean,
        // The rejected mutation, unclassified. Classified here so the modal can
        // render the refusal INLINE — a toast is gone before an operator has
        // finished reading it, and ZONE_NOT_EMPTY is a message they must act on.
        error?: unknown,
    },
) => {

    // The typed-name confirmation for a live zone. Reset whenever the modal
    // opens or the zone changes, or a previous confirmation would carry over
    // and pre-arm the button for a DIFFERENT zone.
    const [typedName, setTypedName] = useState('');

    useEffect(() => {
        setTypedName('');
    }, [opened, zone?.id]);

    // FNP-1365 — read from the stored declaration, before any attempt. Only
    // the ANSWER is used now; the prompt itself moved to the connection page.
    const elevation = useGrantElevation(zone?.connectionId, 'deleteZones');

    if (!zone) {
        return null;
    }

    const block = zoneDeletionBlock(zone);
    const contents = zoneContentsNotice(zone);
    const countState = recordCountState(zone);
    const writeError = error ? classifyWriteError(error) : undefined;

    // ⚠️ A HARD BLOCK ARMS ONLY ON AN EXACT NAME MATCH. Typing the name forces
    // the operator to READ which zone this is — the failure this modal exists
    // to prevent is deleting the wrong zone from a list of similar names.
    const hardBlocked = block?.overridable === false;
    const nameConfirmed = typedName.trim() === zone.name;

    // FNP-1365 — ⚠️ A HARDER BLOCK THAN THE TYPED NAME. Without
    // `route53:DeleteHostedZone` the request cannot succeed whatever they
    // type, so the confirm stays disabled. The line below explains the dead
    // button; it does not re-enable it.
    const permissionMissing = !elevation.granted
        && (elevation.shouldPrompt || elevation.declined);

    const confirmDisabled = isDeleting
        || permissionMissing
        || (hardBlocked && !nameConfirmed);

    // The one-line summary. Delegation is stated as a phrase rather than a
    // badge because it sits inside a sentence of facts, not a table cell.
    const delegation = delegationState(zone);
    const delegationSummary = delegation === 'DELEGATED'
        ? I18n.get('delegated')
        : delegation === 'NOT_DELEGATED'
            ? I18n.get('not delegated')
            : I18n.get('delegation not determined');

    const recordSummary = countState.kind === 'COUNTED'
        ? I18n.get('{count} records').split('{count}').join(String(countState.count))
        : I18n.get('record count unknown');

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={I18n.get('Delete zone')}
            size={'lg'}
            centered={true}
        >
            <Stack gap={'sm'}>

                {/* Zone, contents and delegation in one line — the three facts
                    that identify WHICH zone this is. */}
                <Group gap={'xs'}>
                    <Code>{zone.name}</Code>
                    <Text size={'sm'} c={'dimmed'}>
                        {`· ${recordSummary} · ${delegationSummary}`}
                    </Text>
                </Group>

                {/* ─── GUARD 1: DELEGATION, and the permanence sentence ───
                    The delegated case is the one that takes live traffic
                    offline, so it keeps the typed-name friction. */}
                <Alert
                    color={hardBlocked ? 'red' : 'yellow'}
                    variant={'light'}
                    data-testid={'zone-delete-permanence'}
                >
                    <Stack gap={'xs'}>
                        <Text size={'sm'}>
                            {hardBlocked
                                ? I18n.get('This zone is live. Deleting it takes every hostname in it offline. Cannot be undone.')
                                : I18n.get('Deleting this zone takes every hostname in it offline. Cannot be undone.')}
                        </Text>
                        {hardBlocked && (
                            <TextInput
                                label={I18n.get('Type the zone name to confirm')}
                                placeholder={zone.name}
                                value={typedName}
                                onChange={event => setTypedName(event.currentTarget.value)}
                            />
                        )}
                    </Stack>
                </Alert>

                {/* ─── FNP-1391: WHAT THE ZONE HOLDS — informational, NEVER a
                    gate. A count read at page load goes stale, and blocking on
                    a stale count would lock an operator out of deleting a zone
                    that is genuinely empty. The provider stays the authority. */}
                {contents && (
                    <Alert
                        color={'blue'}
                        variant={'light'}
                        data-testid={'zone-holds-records'}
                    >
                        <Text size={'sm'}>
                            {I18n.get('This zone holds {count} records. The provider refuses while records exist.')
                                .split('{count}').join(String(contents.count))}
                        </Text>
                    </Alert>
                )}

                {/* ─── FNP-1438: THE MISSING PERMISSION, IN ONE LINE ───
                    Permissions are managed on the connection page, so this
                    names the grant and points there rather than negotiating
                    for it here. */}
                {permissionMissing && (
                    <Alert
                        color={'gray'}
                        variant={'light'}
                        data-testid={'zone-delete-permission-missing'}
                    >
                        <Group gap={'xs'} wrap={'nowrap'}>
                            <Text size={'sm'}>
                                {I18n.get('Needs "delete hosted zones", which you did not grant.')}
                            </Text>
                            <Anchor
                                component={NavLink}
                                to={`${providerConnectionsConfig.basePath}/${encodeURIComponent(zone.connectionId)}`}
                                size={'sm'}
                            >
                                {I18n.get('Update permissions')}
                            </Anchor>
                        </Group>
                    </Alert>
                )}

                {/* ─── GUARD 2: THE PROVIDER'S REFUSAL (after the attempt) ───
                    Inline rather than a toast: it names a concrete next step
                    and the operator is still looking at this dialog. */}
                {writeError && (
                    <Alert
                        color={'red'}
                        variant={'light'}
                        title={writeErrorCopy(writeError.kind).title}
                    >
                        <Text size={'sm'}>
                            {writeErrorCopy(writeError.kind).action}
                        </Text>
                    </Alert>
                )}

                <Group justify={'flex-end'}>
                    <Button variant={'default'} onClick={onClose} disabled={isDeleting}>
                        {I18n.get('Cancel')}
                    </Button>
                    <Button
                        color={'red'}
                        onClick={onConfirm}
                        loading={isDeleting}
                        disabled={confirmDisabled}
                    >
                        {/* ZONE_NOT_EMPTY does NOT offer a retry — clicking
                            again cannot succeed until the records are gone. */}
                        {writeError && isWriteErrorRetryable(writeError.kind)
                            ? I18n.get('Try again')
                            : I18n.get('Delete zone')}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
